import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import AdminStatsCards from './AdminStatsCards';
import ClientManagementTable from './ClientManagementTable';
import UsersOverview from './UsersOverview';
import { clientService } from '../../services/clientService';
import { userService } from '../../services/userService';

const { FiSearch, FiFilter, FiDownload, FiLoader } = FiIcons;

const AdminDashboard = ({ user }) => {
  const [allClients, setAllClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterUser, setFilterUser] = useState('all');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch all users
        const usersData = await userService.getAllUsers();
        setUsers(usersData || []);
        
        // Fetch all clients
        const clientsData = await clientService.getAllClients();
        setAllClients(clientsData || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Impossibile caricare i dati. Riprova più tardi.');
        
        // Fallback to mock data for demo
        const mockUsers = [
          {
            id: 1,
            name: 'Marco Verdi',
            email: 'marco@cashflow.com',
            role: 'user'
          },
          {
            id: 2,
            name: 'Sara Rossi',
            email: 'sara@cashflow.com',
            role: 'user'
          },
          {
            id: 3,
            name: 'Luca Bianchi',
            email: 'luca@cashflow.com',
            role: 'user'
          }
        ];

        const mockClients = [
          {
            id: 1,
            contactName: 'Marco Rossi',
            clientName: 'Rossi Marketing SRL',
            contractValue: 5000,
            status: 'nuovo_lead',
            createdAt: new Date('2024-01-15'),
            notes: 'Interessato ai nostri servizi',
            commission: null,
            userId: 1,
            userName: 'Marco Verdi'
          },
          {
            id: 2,
            contactName: 'Laura Bianchi',
            clientName: 'Bianchi Consulting',
            contractValue: 8500,
            status: 'cliente_acquisito',
            createdAt: new Date('2024-01-10'),
            notes: 'Contratto firmato',
            commission: null,
            userId: 2,
            userName: 'Sara Rossi'
          },
          {
            id: 3,
            contactName: 'Giuseppe Verdi',
            clientName: 'Verdi & Associates',
            contractValue: 12000,
            status: 'pagato',
            createdAt: new Date('2024-01-05'),
            notes: 'Progetto completato',
            commission: 1200,
            userId: 1,
            userName: 'Marco Verdi'
          },
          {
            id: 4,
            contactName: 'Anna Ferrari',
            clientName: 'Ferrari Industries',
            contractValue: 15000,
            status: 'in_trattativa',
            createdAt: new Date('2024-01-12'),
            notes: 'Negoziazione in corso',
            commission: null,
            userId: 3,
            userName: 'Luca Bianchi'
          }
        ];
        
        setUsers(mockUsers);
        setAllClients(mockClients);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleStatusUpdate = async (clientId, newStatus) => {
    try {
      await clientService.updateClientStatus(clientId, newStatus);
      
      setAllClients(prev =>
        prev.map(client =>
          client.id === clientId ? { ...client, status: newStatus } : client
        )
      );
    } catch (err) {
      console.error('Error updating status:', err);
      alert('Errore durante l\'aggiornamento dello stato. Riprova più tardi.');
    }
  };

  const handleCommissionUpdate = async (clientId, commission) => {
    try {
      await clientService.addCommission(clientId, commission);
      
      setAllClients(prev =>
        prev.map(client =>
          client.id === clientId
            ? { ...client, commission, status: 'commissionato' }
            : client
        )
      );
    } catch (err) {
      console.error('Error adding commission:', err);
      alert('Errore durante l\'aggiunta della commissione. Riprova più tardi.');
    }
  };

  const handleDeleteClient = async (clientId) => {
    try {
      await clientService.deleteClient(clientId);
      setAllClients(prev => prev.filter(client => client.id !== clientId));
    } catch (err) {
      console.error('Error deleting client:', err);
      alert('Errore durante l\'eliminazione del cliente. Riprova più tardi.');
    }
  };

  const filteredClients = allClients.filter(client => {
    const matchesSearch =
      client.contactName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.userName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || client.status === filterStatus;
    const matchesUser = filterUser === 'all' || client.userId?.toString() === filterUser;
    
    return matchesSearch && matchesStatus && matchesUser;
  });

  const statusOptions = [
    { value: 'all', label: 'Tutti gli Stati' },
    { value: 'nuovo_lead', label: 'Nuovo Lead' },
    { value: 'in_trattativa', label: 'In Trattativa' },
    { value: 'cliente_acquisito', label: 'Cliente Acquisito' },
    { value: 'pagato', label: 'Pagato' },
    { value: 'commissionato', label: 'Commissionato' }
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="mb-8"
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Controllo Totale CashFlow</h1>
        <p className="text-gray-600">Gestisci tutti i clienti, stati e commissioni del team</p>
      </motion.div>

      {/* Loading State */}
      {loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center py-12"
        >
          <div className="text-emerald-400 mb-4">
            <SafeIcon icon={FiLoader} className="text-4xl mx-auto animate-spin" />
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">Caricamento dati...</h3>
        </motion.div>
      )}

      {/* Error State */}
      {error && !loading && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg mb-6"
        >
          {error}
        </motion.div>
      )}

      {!loading && !error && (
        <>
          {/* Stats */}
          <AdminStatsCards clients={allClients} users={users} />

          {/* Users Overview */}
          <UsersOverview users={users} clients={allClients} />

          {/* Filters */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6"
          >
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex flex-col sm:flex-row gap-4 flex-1">
                {/* Search */}
                <div className="relative flex-1 max-w-md">
                  <SafeIcon
                    icon={FiSearch}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
                  />
                  <input
                    type="text"
                    placeholder="Cerca clienti, aziende o venditori..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                  />
                </div>

                {/* Status Filter */}
                <div className="relative">
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                  >
                    {statusOptions.map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                {/* User Filter */}
                <div className="relative">
                  <select
                    value={filterUser}
                    onChange={(e) => setFilterUser(e.target.value)}
                    className="pl-3 pr-8 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 bg-white"
                  >
                    <option value="all">Tutti i Venditori</option>
                    {users.map(user => (
                      <option key={user.id} value={user.id.toString()}>
                        {user.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Export Button */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="flex items-center space-x-2 bg-gray-500 text-white px-4 py-2 rounded-lg hover:bg-gray-600 transition-colors"
              >
                <SafeIcon icon={FiDownload} />
                <span>Esporta</span>
              </motion.button>
            </div>
          </motion.div>

          {/* Clients Table */}
          <ClientManagementTable
            clients={filteredClients}
            onStatusUpdate={handleStatusUpdate}
            onCommissionUpdate={handleCommissionUpdate}
            onDeleteClient={handleDeleteClient}
          />
        </>
      )}
    </div>
  );
};

export default AdminDashboard;